// src/pages/Clientes.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./Clientes.css";

// Componentes e iconos compartidos. Los iconos vienen de un solo lugar
// (componentes/base/iconos.js) y se nombran por significado, para que la
// misma acción se vea igual en todas las pantallas.
import {
  AccionFila, BarraFiltros, Boton, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";

// MODALES FIBRA
import ModalAgregarCliente from "../components/ModalAgregarCliente";
import ModalPagoCliente from "../components/ModalPagoCliente";
import ModalPagos from "../components/ModalPagos";
import TicketPago from "../components/TicketPago";
import ModalCambioPlan from "../components/ModalCambioPlan";
import ModalPagoManual from "../components/ModalPagoManual";
import ModalActivarOnt from "../components/ModalActivarOnt";
import ProcesoPago from "../components/ProcesoPago";
//MODALES WIFI
import ModalAgregarClienteWifi from "../components/ModalAgregarClienteWifi";
import ModalPagoClienteWifi from "../components/ModalPagoClienteWifi";
import ModalPagosWifi from "../components/ModalPagosWifi";
import ModalMudarClienteWifi from "../components/ModalMudarClienteWifi";
import ModalPagoManualWifi from "../components/ModalPagoManualWifi";


// TOAST
import Toast from "../components/Toast";
import { authFetch } from "../utils/authFetch";

/** helpers UI editable (FUERA del componente para no remount y no mover el cursor) */
const EditableDesktopCell = React.memo(function EditableDesktopCell({
  row,
  campo,
  valor,
  render,
  tipo,
  editando,
  activarEdicion,
  editarValor,
  guardarEdicion,
  cancelarEdicion,
}) {
  const editing = editando.id === row.id && editando.campo === campo && editando.tipo === tipo;

  // Mantener caret (cursor) estable al re-render
  const inputRef = useRef(null);
  const caretRef = useRef(null);

  useLayoutEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;

    if (caretRef.current != null) {
      try {
        el.setSelectionRange(caretRef.current, caretRef.current);
      } catch {}
    }
  }, [editing, editando.valor]);

  return (
    <td
      className={editing ? "cell-editing" : ""}
      onDoubleClick={() => activarEdicion(row.id, campo, valor ?? "", tipo)}
    >
      {editing ? (
        <input
          ref={inputRef}
          className="cell-edit-input"
          autoFocus
          value={editando.valor}
          onChange={(e) => {
            caretRef.current = e.target.selectionStart;
            editarValor(e);
          }}
          onSelect={(e) => {
            caretRef.current = e.target.selectionStart;
          }}
          onBlur={guardarEdicion}
          onKeyDown={(e) => {
            if (e.key === "Enter") guardarEdicion();
            if (e.key === "Escape") cancelarEdicion();
          }}
        />
      ) : render ? (
        render()
      ) : (
        valor ?? "-"
      )}
    </td>
  );
});

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const porPagina = 10;

  const [mostrarModal, setMostrarModal] = useState(false);
  const [modalPago, setModalPago] = useState(null);
  const [toast, setToast] = useState(null);
  const [modalPagoManual, setModalPagoManual] = useState(null);
  const [modalActivarOnt, setModalActivarOnt] = useState(null);
  const [procesoPago, setProcesoPago] = useState(null); // overlay animado del pago
  const [ticketPendiente, setTicketPendiente] = useState(null); // ticket a mostrar al cerrar el resultado
  const [modalPagosInfo, setModalPagosInfo] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [modalPlan, setModalPlan] = useState(null);
  const [pagoLoading, setPagoLoading] = useState(false);
  const [pagoMsg, setPagoMsg] = useState("");

  // WIFI
  const [clientesWifi, setClientesWifi] = useState([]);
  const [busquedaWifi, setBusquedaWifi] = useState("");
  const [paginaWifi, setPaginaWifi] = useState(1);
  const [mostrarModalWifi, setMostrarModalWifi] = useState(false);
  const [modalPagoWifi, setModalPagoWifi] = useState(null);
  const [modalPagosInfoWifi, setModalPagosInfoWifi] = useState(null);
  const [modalPagoManualWifi, setModalPagoManualWifi] = useState(null);
  const [modalMudarClienteWifi, setModalMudarClienteWifi] = useState(null);


  function abrirCambioPlan(cliente) {
    setModalPlan(cliente);
  }

  function abrirMudarClienteWifi(cliente) {
    setModalMudarClienteWifi(cliente);
  }

  // ✅ TAB NAV
  const [tab, setTab] = useState("fibra"); // 'fibra' | 'wifi'

  useEffect(() => {
  setMostrarModal(false);
  setMostrarModalWifi(false);
  setModalPago(null);
  setModalPagosInfo(null);
  setModalPagoManual(null);
  setModalPlan(null);

  setModalPagoWifi(null);
  setModalPagosInfoWifi(null);
  setModalPagoManualWifi(null);
  setModalMudarClienteWifi(null);
  setEditando({ tipo: tab, id: null, campo: "", valor: "" });

}, [tab]);

  /* ======================================================
     ESTADO DE EDICIÓN INLINE
     + tipo para saber si es fibra o wifi
  ====================================================== */
  const [editando, setEditando] = useState({
    tipo: 'fibra', // 'wifi'
    id: null,
    campo: "",
    valor: "",
  });

  function activarEdicion(id, campo, valor, tipoOverride) {
    const tipo = tipoOverride || tab; // si no mandas tipo, usa tab actual
    setEditando({ tipo, id, campo, valor });
  }

  function cancelarEdicion() {
    setEditando({ tipo: tab, id: null, campo: "", valor: "" });
  }

  function editarValor(e) {
    setEditando((prev) => ({ ...prev, valor: e.target.value }));
  }

  async function guardarEdicion() {
    if (!editando.id || !editando.campo) return;

    const esWifi = editando.tipo === "wifi";
    const url = esWifi ? "/api/clientes_wifi/editar" : "/api/clientes/editar";

    try {
      const res = await authFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: editando.id,
          campo: editando.campo,
          valor: editando.valor,
        }),
      });

      const data = await res.json();

      if (data.ok) {
        setToast({ mensaje: "Actualizado", tipo: "success" });
        cancelarEdicion();
        if (esWifi) cargarClientesWifi();
        else cargarClientes();
      } else {
        setToast({ mensaje: "Error: " + (data.mensaje || data.error || "No se pudo editar"), tipo: "error" });
      }
    } catch (e) {
      setToast({ mensaje: "Error actualizando", tipo: "error" });
    }
  }

  /* ======================================================
     GENERAR PPPoE PASS (solo fibra)
  ====================================================== */
  function generarPPPoEPass() {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let aleatorio = "";
    for (let i = 0; i < 7; i++) {
      aleatorio += chars[Math.floor(Math.random() * chars.length)];
    }
    return "WF" + aleatorio;
  }

  /* ======================================================
     CARGAR CLIENTES FIBRA
  ====================================================== */
  async function cargarClientes() {
    try {
      const res = await authFetch("/api/clientes");
      const data = await res.json();
      if (data.ok) setClientes(data.clientes);
    } catch (error) {
      console.log("Error cargando clientes:", error);
    }
  }

  useEffect(() => {
    cargarClientes();
  }, []);

  /* ======================================================
     CARGAR CLIENTES WIFI
  ====================================================== */
  async function cargarClientesWifi() {
    try {
      const res = await authFetch("/api/clientes_wifi");
      const data = await res.json();
      if (data.ok) {
        // igual que fibra: agregamos open false para expandir
        const rows = (data.clientes || []).map((r) => ({ ...r, open: false }));
        setClientesWifi(rows);
      } else {
        console.log("clientes_wifi error:", data.error);
      }
    } catch (error) {
      console.log("Error cargando clientes wifi:", error);
    }
  }

  useEffect(() => {
    if (tab === "wifi") cargarClientesWifi();
  }, [tab]);

  /* ======================================================
     REGISTRAR PAGO DIAS RESTANTES + OTROS (FIBRA)
  ====================================================== */
  async function registrarPagoManual(payload) {
    try {
      setPagoLoading(true);
      setPagoMsg("Registrando pago...");

      // ✅ a meses -> ADEUDOS (sin ticket)
      if (
        (payload.concepto === "modem_extra" || payload.concepto === "cambio_domicilio") &&
        payload.modalidad === "meses"
      ) {
        const meses = Number(payload.meses_adeudo || 0);

        const resA = await authFetch("/api/adeudos/crear", { // (haz /crear-wifi para wifi si lo quieres separado)
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cliente_id: payload.cliente_id,
            monto_total: payload.monto,
            meses,
            fecha_inicio: payload.fecha_pago_hora?.slice(0, 10), // YYYY-MM-DD
            concepto: payload.concepto === "modem_extra" ? 1 : 0, // 1=modem, 0=cambio domicilio
          }),
        });

        const dataA = await resA.json();
        if (!dataA.ok) {
          setToast({ mensaje: dataA.error || "No se pudo registrar el adeudo", tipo: "error" });
          return;
        }

        setToast({ mensaje: "Adeudo registrado", tipo: "success" });
        setModalPagoManual(null);
        await cargarClientes();
        return;
      }


      if (payload.concepto !== "dias_restantes") {
        const refMap = {
          cable_extra: "cable extra",
          modem_extra: "modem extra",
          reparacion: "reparacion",
          adeudo: "adeudo",
          cambio_domicilio: "cambio de domicilio"
        };

        const res = await authFetch("/api/pagos/caja/registrar_y_ticket", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cliente_id: payload.cliente_id,
            monto: payload.monto,
            forma_pago: payload.forma_pago,
            referencia: refMap[payload.concepto] || null,
            fecha_pago_hora: payload.fecha_pago_hora,
          }),
        });

        const data = await res.json();
        if (!data.ok) {
          setToast({ mensaje: data.error || "Error al registrar en caja", tipo: "error" });
          return;
        }

        setTicketData(data.ticket);
        await cargarClientes();
        setToast({ mensaje: "Registrado en caja", tipo: "success" });
        setModalPagoManual(null);
        return;
      }

      const res = await authFetch("/api/pagos/registrar_manual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cliente_id: payload.cliente_id,
          mes: payload.mes,
          anio: payload.anio,
          monto: payload.monto,
          forma_pago: payload.forma_pago,
          referencia: payload.referencia,
          fecha_pago_hora: payload.fecha_pago_hora,
        }),
      });

      const data = await res.json();
      if (!data.ok) {
        setToast({ mensaje: data.error || "Error al registrar pago", tipo: "error" });
        return;
      }

      setPagoMsg("Pago registrado. Generando ticket...");

      const resT = await authFetch(`/api/pagos/ticket/recibo/${payload.cliente_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pagos: payload._ticketPagos || [{ mes: payload.mes, anio: payload.anio }],
          instalacion_pagada: false,
        }),
      });

      const ticketData2 = await resT.json();
      if (ticketData2.ok) setTicketData(ticketData2.ticket);
      else setToast({ mensaje: ticketData2.error || "Ticket no generado", tipo: "error" });

      await cargarClientes();
      setToast({ mensaje: "Pago registrado", tipo: "success" });
      setModalPagoManual(null);
    } catch (e) {
      console.log("ERROR registrarPagoManual:", e);
      setToast({ mensaje: "Error de red", tipo: "error" });
    } finally {
      setPagoLoading(false);
      setPagoMsg("");
    }
  }

/* ======================================================
     REGISTRAR PAGO DIAS RESTANTES + OTROS (WIFI)
  ====================================================== */
  async function registrarPagoManualWifi(payload) {
    try {
      setPagoLoading(true);
      setPagoMsg("Registrando pago...");

      // ✅ a meses -> ADEUDOS (sin ticket)
      if (
        (payload.concepto === "modem_extra" || payload.concepto === "cambio_domicilio") &&
        payload.modalidad === "meses"
      ) {
        const meses = Number(payload.meses_adeudo || 0);

        const resA = await authFetch("/api/adeudos/crear-wifi", { // (haz /crear-wifi para wifi si lo quieres separado)
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cliente_id: payload.cliente_id,
            monto_total: payload.monto,
            meses,
            fecha_inicio: payload.fecha_pago_hora?.slice(0, 10), // YYYY-MM-DD
            concepto: payload.concepto === "modem_extra" ? 1 : 0, // 1=modem, 0=cambio domicilio
          }),
        });

        const dataA = await resA.json();
        if (!dataA.ok) {
          setToast({ mensaje: dataA.error || "No se pudo registrar el adeudo", tipo: "error" });
          return;
        }

        setToast({ mensaje: "Adeudo registrado", tipo: "success" });
        setModalPagoManualWifi(null);
        await cargarClientesWifi();
        return;
      }


      if (payload.concepto !== "dias_restantes") {
        const refMap = {
          cable_extra: "cable extra",
          modem_extra: "modem extra",
          reparacion: "reparacion",
          adeudo: "adeudo",
          cambio_domicilio: "cambio de domicilio"
        };

        const res = await authFetch("/api/pagos/caja/registrar_y_ticket-wifi", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cliente_id: payload.cliente_id,
            monto: payload.monto,
            forma_pago: payload.forma_pago,
            referencia: refMap[payload.concepto] || null,
            fecha_pago_hora: payload.fecha_pago_hora,
          }),
        });

        const data = await res.json();
        if (!data.ok) {
          setToast({ mensaje: data.error || "Error al registrar en caja", tipo: "error" });
          return;
        }

        setTicketData(data.ticket);
        await cargarClientesWifi();
        setToast({ mensaje: "Registrado en caja", tipo: "success" });
        setModalPagoManualWifi(null);
        return;
      }

      const res = await authFetch("/api/pagos/registrar_manual-wifi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cliente_id: payload.cliente_id,
          mes: payload.mes,
          anio: payload.anio,
          monto: payload.monto,
          forma_pago: payload.forma_pago,
          referencia: payload.referencia,
          fecha_pago_hora: payload.fecha_pago_hora,
        }),
      });

      const data = await res.json();
      if (!data.ok) {
        setToast({ mensaje: data.error || "Error al registrar pago", tipo: "error" });
        return;
      }

      setPagoMsg("Pago registrado. Generando ticket...");

      const resT = await authFetch(`/api/pagos/ticket/recibo-wifi/${payload.cliente_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pagos: payload._ticketPagos || [{ mes: payload.mes, anio: payload.anio }],
          instalacion_pagada: false,
        }),
      });

      const ticketData2 = await resT.json();
      if (ticketData2.ok) setTicketData(ticketData2.ticket);
      else setToast({ mensaje: ticketData2.error || "Ticket no generado", tipo: "error" });

      await cargarClientesWifi();
      setToast({ mensaje: "Pago registrado", tipo: "success" });
      setModalPagoManualWifi(null);
    } catch (e) {
      console.log("ERROR registrarPagoManualWifi:", e);
      setToast({ mensaje: "Error de red", tipo: "error" });
    } finally {
      setPagoLoading(false);
      setPagoMsg("");
    }
  }

  /* ======================================================
     REGISTRAR PAGO COMPLETO (FIBRA)
  ====================================================== */
  async function registrarPago(pagoData) {
    // ¿El cliente estaba suspendido? (la reconexión es el momento crítico)
    const estabaCortado = modalPago?.estado === "cortado";
    // Cerrar el modal de pago YA: la animación la lleva SOLO ProcesoPago
    // (si no, su spinner "Procesando…" se ve por debajo = doble animación).
    setModalPago(null);
    try {
      setPagoLoading(true);
      setPagoMsg("Registrando pago...");

      // 🎬 Overlay animado: pasos según el caso
      setProcesoPago({
        fase: "trabajando",
        titulo: estabaCortado ? "Reconectando servicio" : "Procesando pago",
        pasos: estabaCortado
          ? ["Registrando pago…", "Enviando reconexión al OLT…"]
          : ["Registrando pago…", "Guardando en caja…"],
      });

      const res = await authFetch("/api/pagos/registrar_completo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pagoData),
      });

      const data = await res.json();

      if (!data.ok) {
        setProcesoPago(null);
        setToast({ mensaje: data.error || "Error al registrar pago", tipo: "error" });
        return;
      }

      // Generar el ticket AHORA, pero mostrarlo AL FINAL (no antes del resultado)
      const resT = await authFetch(`/api/pagos/ticket/recibo/${pagoData.cliente_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pagos: pagoData.meses || [],
          instalacion_pagada: !!pagoData.pagar_instalacion,
          instalacion_modo: pagoData.instalacion_modo,
          adeudos_pago: pagoData.adeudos_pago || [],
        }),
      });
      const ticketData2 = await resT.json();
      const ticket = ticketData2.ok ? ticketData2.ticket : null;

      // Actualizar estado del cliente en la lista
      const recon = data.reconexion;
      if (recon && recon.ok === true && /reconect/i.test(recon.mensaje || "")) {
        setClientes((prev) =>
          prev.map((c) => (c.id === pagoData.cliente_id ? { ...c, estado: "activo" } : c))
        );
      }
      await cargarClientes();

      if (estabaCortado) {
        // Verificación REAL de la ONT (mismo diagnóstico por serial del ⏻)
        setProcesoPago({
          fase: "trabajando",
          titulo: "Verificando reconexión",
          pasos: ["Consultando la ONT en el OLT…", "Leyendo estado real…"],
        });
        let diag = null;
        try {
          const rd = await authFetch(`/api/clientes/${pagoData.cliente_id}/diagnostico_activacion`);
          const dd = await rd.json();
          if (dd.ok) diag = dd;
        } catch (e) {
          console.log("verificación post-pago falló:", e);
        }
        // El ticket queda PENDIENTE: se muestra al cerrar el resultado.
        setTicketPendiente(ticket);
        setProcesoPago({
          fase: "resultado",
          reconexion: data.reconexion || { ok: false },
          diag,
        });
        // Si el ticket no se generó, avisar (antes fallaba en silencio)
        if (!ticket) {
          setToast({
            mensaje: "Pago registrado, pero el TICKET no se generó: " +
                     (ticketData2.error || "error desconocido"),
            tipo: "error",
          });
        }
      } else {
        // Pago normal: cerrar overlay, mostrar ticket y confirmar
        setProcesoPago(null);
        if (ticket) setTicketData(ticket);
        else setToast({ mensaje: ticketData2.error || "Ticket no generado", tipo: "error" });
      }
    } catch (e) {
      console.log("ERROR:", e);
      setProcesoPago(null);
      setToast({ mensaje: "Error de red", tipo: "error" });
    } finally {
      setPagoLoading(false);
      setPagoMsg("");
    }
  }

  /* ======================================================
     REGISTRAR PAGO COMPLETO (WIFI)
  ====================================================== */
  async function registrarPagoWifi(pagoData) {
    // WiFi: estado 0 = cortado (la reconexión se hace en el MikroTik)
    const estabaCortado = Number(modalPagoWifi?.estado ?? 1) === 0;
    // Cerrar el modal de pago YA (evita el spinner doble por debajo)
    setModalPagoWifi(null);
    try {
      setPagoLoading(true);
      setPagoMsg("Registrando pago...");

      setProcesoPago({
        fase: "trabajando",
        titulo: estabaCortado ? "Reconectando servicio" : "Procesando pago",
        pasos: estabaCortado
          ? ["Registrando pago…", "Reconectando en MikroTik…"]
          : ["Registrando pago…", "Guardando en caja…"],
      });

      const res = await authFetch("/api/pagos/registrar_completo-wifi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pagoData),
      });

      const data = await res.json();

      if (!data.ok) {
        setProcesoPago(null);
        setToast({ mensaje: data.error || "Error al registrar pago", tipo: "error" });
        return;
      }

      // Generar ticket ahora, mostrarlo AL FINAL
      const resT = await authFetch(`/api/pagos/ticket/recibo-wifi/${pagoData.cliente_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pagos: pagoData.meses || [],
          instalacion_pagada: !!pagoData.pagar_instalacion,
          instalacion_modo: pagoData.instalacion_modo,
          adeudos_pago: pagoData.adeudos_pago || [],
        }),
      });
      const ticketData2 = await resT.json();
      const ticket = ticketData2.ok ? ticketData2.ticket : null;

      await cargarClientesWifi();

      if (estabaCortado) {
        // WiFi no tiene ONT: el resultado muestra lo que confirmó el MikroTik
        setTicketPendiente(ticket);
        setProcesoPago({ fase: "resultado", reconexion: data.reconexion || { ok: false }, diag: null });
        if (!ticket) {
          setToast({
            mensaje: "Pago registrado, pero el TICKET no se generó: " +
                     (ticketData2.error || "error desconocido"),
            tipo: "error",
          });
        }
      } else {
        setProcesoPago(null);
        if (ticket) setTicketData(ticket);
        else setToast({ mensaje: ticketData2.error || "Ticket no generado", tipo: "error" });
      }
    } catch (e) {
      console.log("ERROR registrarPagoWifi:", e);
      setProcesoPago(null);
      setToast({ mensaje: "Error de red", tipo: "error" });
    } finally {
      setPagoLoading(false);
      setPagoMsg("");
    }
  }


  /* ======================================================
     FILTRO FIBRA
  ====================================================== */
  const filtrados = useMemo(() => {
    const raw = busqueda.trim().toLowerCase();
    if (!raw) return clientes;

    const tokens = raw.split(/\s+/).filter(Boolean);

    return clientes.filter((c) => {
      const idStr = String(c.id ?? "");
      const haystack = [
        c.id,
        c.nombre,
        c.apellidos,
        c.direccion,
        c.telefono,
        c.colonia,
        c.num_serie,
        c.plan,
        c.estado,
        c.pppoe_user,
        c.municipio,
        c.comentario,
      ]
        .map((v) => (v === null || v === undefined ? "" : String(v)))
        .join(" ")
        .toLowerCase();

      return tokens.every((t0) => {
        let t = t0;
        if (t.startsWith("#")) t = t.slice(1);
        if (t.startsWith("id:") || t.startsWith("id=")) t = t.slice(3);
        if (!t) return true;
        if (/^\d+$/.test(t) && idStr.includes(t)) return true;
        return haystack.includes(t);
      });
    });
  }, [clientes, busqueda]);

  /* ======================================================
     FILTRO WIFI
  ====================================================== */
  const filtradosWifi = useMemo(() => {
    const raw = busquedaWifi.trim().toLowerCase();
    if (!raw) return clientesWifi;

    const tokens = raw.split(/\s+/).filter(Boolean);

    return clientesWifi.filter((c) => {
      const idStr = String(c.id ?? "");
      const haystack = [
        c.id,
        c.nombre,
        c.apellidos,
        c.direccion,
        c.telefono,
        c.colonia,
        c.municipio,
        c.plan,
        c.costo,
        c.nodo,
        c.ip,
        c.estado,
        c.equipo,
        c.comentario,
      ]
        .map((v) => (v === null || v === undefined ? "" : String(v)))
        .join(" ")
        .toLowerCase();

      return tokens.every((t0) => {
        let t = t0;
        if (t.startsWith("#")) t = t.slice(1);
        if (t.startsWith("id:") || t.startsWith("id=")) t = t.slice(3);
        if (!t) return true;
        if (/^\d+$/.test(t) && idStr.includes(t)) return true;
        return haystack.includes(t);
      });
    });
  }, [clientesWifi, busquedaWifi]);

  /* ======================================================
     PAGINACIÓN (utilidades)
  ====================================================== */
  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function getPageItems(current, total) {
    const delta = 2;
    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);
    if (left > 2) pages.push("...");

    for (let p = left; p <= right; p++) pages.push(p);

    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);

    return pages;
  }

  /* ======================================================
     PAGINACIÓN FIBRA
  ====================================================== */
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
  const clientesPagina = filtrados.slice((pagina - 1) * porPagina, pagina * porPagina);

  const pageItems = useMemo(() => getPageItems(pagina, totalPaginas), [pagina, totalPaginas]);

  useEffect(() => {
    setPagina((p) => clamp(p, 1, totalPaginas));
  }, [totalPaginas]);

  /* ======================================================
     PAGINACIÓN WIFI
  ====================================================== */
  const totalPaginasWifi = Math.max(1, Math.ceil(filtradosWifi.length / porPagina));
  const clientesPaginaWifi = filtradosWifi.slice(
    (paginaWifi - 1) * porPagina,
    paginaWifi * porPagina
  );

  const pageItemsWifi = useMemo(
    () => getPageItems(paginaWifi, totalPaginasWifi),
    [paginaWifi, totalPaginasWifi]
  );

  useEffect(() => {
    setPaginaWifi((p) => clamp(p, 1, totalPaginasWifi));
  }, [totalPaginasWifi]);

  /* ======================================================
     EXPANDIR FIBRA
  ====================================================== */
  function toggleFila(id) {
    setClientes((prev) => prev.map((c) => (c.id === id ? { ...c, open: !c.open } : c)));
  }

  /* ======================================================
     EXPANDIR WIFI
  ====================================================== */
  function toggleFilaWifi(id) {
    setClientesWifi((prev) => prev.map((c) => (c.id === id ? { ...c, open: !c.open } : c)));
  }

  /* ======================================================
     GUARDAR NUEVO CLIENTE (FIBRA)
  ====================================================== */
  async function guardarCliente(form) {
    try {
      const payload = { ...form, pppoe_pass: form.pppoe_pass || generarPPPoEPass() };

      const res = await authFetch("/api/clientes/nuevo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.ok) {
        setToast({ mensaje: "Error: " + (data.mensaje || "No se pudo guardar"), tipo: "error" });
        return;
      }

      const inst = payload.instalacion;
      const esContado =
        inst && Number(inst.costo) > 0 && String(inst.tipo).toLowerCase() === "contado";

      if (esContado) {
        const resT = await authFetch(`/api/pagos/ticket/instalacion_contado/${payload.id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ monto: Number(inst.costo) }),
        });

        const t = await resT.json();
        if (t.ok) setTicketData(t.ticket);
        else setToast({ mensaje: "Cliente guardado, pero no se generó ticket", tipo: "error" });
      }

      setToast({ mensaje: "Cliente guardado exitosamente", tipo: "success" });
      setMostrarModal(false);
      await cargarClientes();
    } catch (error) {
      console.log("ERROR guardarCliente:", error);
      setToast({ mensaje: "Error agregando cliente", tipo: "error" });
    }
  }

  /* ======================================================
     MUDAR CLIENTE DE WIFI A FIBRA
  ====================================================== */
  async function mudarClienteWifi(form) {
    try {
      const payload = { ...form, pppoe_pass: form.pppoe_pass || generarPPPoEPass() };

      const res = await authFetch("/api/clientes_wifi/mudanza-nuevo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.ok) {
        setToast({ mensaje: "Error: " + (data.mensaje || "No se pudo guardar"), tipo: "error" });
        return;
      }

      const inst = payload.instalacion;
      const esContado =
        inst && Number(inst.costo) > 0 && String(inst.tipo).toLowerCase() === "contado";

      if (esContado) {
        const resT = await authFetch(`/api/pagos/ticket/instalacion_contado/${payload.id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ monto: Number(inst.costo) }),
        });

        const t = await resT.json();
        if (t.ok) setTicketData(t.ticket);
        else setToast({ mensaje: "Cliente guardado, pero no se generó ticket", tipo: "error" });
      }

      setToast({ mensaje: "Cliente guardado exitosamente", tipo: "success" });
      setModalMudarClienteWifi(null); // ✅ cierra el modal de mudanza
      setTab("fibra");
      await cargarClientesWifi();
    } catch (error) {
      console.log("ERROR mudarClienteWifi:", error);
      setToast({ mensaje: "Error agregando cliente", tipo: "error" });
    }
  }

  /* ======================================================
     GUARDAR NUEVO CLIENTE (WIFI)
  ====================================================== */
  async function guardarClienteWifi(payload) {
    try {
      const res = await authFetch("/api/clientes_wifi/nuevo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!data.ok) {
        setToast({ mensaje: "Error: " + (data.mensaje || data.error || "No se pudo guardar"), tipo: "error" });
        return;
      }

      setToast({ mensaje: "Cliente WiFi guardado", tipo: "success" });
      setMostrarModalWifi(false);
      await cargarClientesWifi();

      // Instalación de contado: el cobro entra a caja_wifi durante el alta,
      // así que hay dinero recibido y el cliente necesita su comprobante.
      // El alta WiFi no emitía ninguno (el de fibra sí, con su propio
      // endpoint) y el cliente se iba sin ticket.
      // A meses no se cobra nada ahora: se paga con las mensualidades.
      const inst = payload?.instalacion;
      const esContado =
        inst && Number(inst.costo) > 0 && String(inst.tipo).toLowerCase() === "contado";

      if (esContado) {
        // Se usa recibo-wifi (no ticket/instalacion_contado): ese consulta la
        // tabla `clientes`, donde un cliente WiFi no existe.
        const resT = await authFetch(`/api/pagos/ticket/recibo-wifi/${payload.id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pagos: [], instalacion_pagada: true }),
        });
        const tj = await resT.json();
        if (tj.ok) setTicketData(tj.ticket);
        else
          setToast({
            mensaje: "Cliente guardado y cobro registrado, pero el TICKET no se generó: " +
                     (tj.error || "error desconocido"),
            tipo: "error",
          });
      }
    } catch (e) {
      setToast({ mensaje: "Error de red guardando WiFi", tipo: "error" });
    }
  }

  /* ======================================================
     RENDER
  ====================================================== */
  return (
    <div className="clientes-page">
      {/* La animación del pago la maneja ProcesoPago (overlay único). */}
      <EncabezadoPagina
        icono={Iconos.clientes}
        titulo="Clientes"
        resumen={
          tab === "fibra"
            ? `${clientes.length} de fibra`
            : `${clientesWifi.length} de WiFi`
        }
        acciones={
          <Boton
            icono={Iconos.agregar}
            onClick={() => (tab === "fibra" ? setMostrarModal(true) : setMostrarModalWifi(true))}
          >
            Agregar cliente {tab === "fibra" ? "fibra" : "WiFi"}
          </Boton>
        }
      />

      {/* ===========================
          FIBRA (intacto)
      =========================== */}
      {tab === "fibra" && (
        <>
          <BarraFiltros
            valor={busqueda}
            placeholder="Buscar por nombre, ID, teléfono…"
            onBuscar={(v) => {
              setBusqueda(v);
              setPagina(1);
            }}
            filtros={[
              { valor: "fibra", etiqueta: "Fibra" },
              { valor: "wifi", etiqueta: "WiFi" },
            ]}
            filtroActivo={tab}
            onFiltro={setTab}
          />

          <table className="clientes-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Cliente</th>
                <th>Apellidos</th>
                <th>Dirección</th>
                <th>Teléfono</th>
                <th>Colonia</th>
                <th>Plan</th>
                <th>Costo</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {clientesPagina.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td className="expand-cell">
                      <button className="expand-btn" onClick={() => toggleFila(c.id)}
                        aria-label={c.open ? "Ocultar detalles" : "Ver detalles"}>
                        {c.open
                          ? <Iconos.desplegar size={16} strokeWidth={2} />
                          : <Iconos.siguiente size={16} strokeWidth={2} />}
                      </button>
                    </td>

                    <td>{c.id}</td>

                    <EditableDesktopCell
                      row={c}
                      tipo="fibra"
                      campo="nombre"
                      valor={`${c.nombre || ""}`.trim()}
                      render={() => `${c.nombre || ""}`.trim()}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="fibra"
                      campo="apellidos"
                      valor={c.apellidos || ""}
                      render={() => c.apellidos || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="fibra"
                      campo="direccion"
                      valor={c.direccion || ""}
                      render={() => c.direccion || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="fibra"
                      campo="telefono"
                      valor={c.telefono || ""}
                      render={() => c.telefono || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="fibra"
                      campo="colonia"
                      valor={c.colonia || ""}
                      render={() => c.colonia || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />

                    <td>{c.plan}</td>
                    <td>${c.costo}</td>

                    <td>
                      <Insignia estado={c.estado} />
                    </td>

                    <td>
                      <div className="acciones">
                      <Boton onClick={() => setModalPago(c)} disabled={pagoLoading}>
                        Pagar
                      </Boton>

                      {/* Los iconos ya no llevan color propio: lo toman del
                          tema, y así se ven igual en toda la app. */}
                      <AccionFila
                        icono={Iconos.pagos}
                        titulo="Historial de pagos"
                        onClick={() => setModalPagosInfo(c)}
                        disabled={pagoLoading}
                      />
                      <AccionFila
                        icono={Iconos.planes}
                        titulo="Cambiar de plan"
                        onClick={() => abrirCambioPlan(c)}
                        disabled={pagoLoading}
                      />
                      <AccionFila
                        icono={Iconos.documento}
                        titulo="Pago manual"
                        onClick={() => setModalPagoManual(c)}
                        disabled={pagoLoading}
                      />
                      <AccionFila
                        icono={Iconos.encender}
                        titulo="Verificar ONT (activar / desactivar)"
                        peligro
                        onClick={() => setModalActivarOnt(c)}
                        disabled={pagoLoading}
                      />
                      </div>
                    </td>
                  </tr>

                  {/* ✅ EXPANDIDO EN FILAS (vertical) */}
                  {c.open && (
                    <>
                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Municipio</strong></td>
                        <td colSpan="7">
                          <span
                            className="editable-inline"
                            onDoubleClick={() => activarEdicion(c.id, "municipio", c.municipio || "", "fibra")}
                          >
                            {editando.id === c.id && editando.campo === "municipio" && editando.tipo === "fibra" ? (
                              <input
                                className="cell-edit-input"
                                autoFocus
                                value={editando.valor}
                                onChange={editarValor}
                                onBlur={guardarEdicion}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") guardarEdicion();
                                  if (e.key === "Escape") cancelarEdicion();
                                }}
                              />
                            ) : (
                              c.municipio || "-"
                            )}
                          </span>
                        </td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>PPPoE</strong></td>
                        <td colSpan="7">{`${c.pppoe_user} / ${c.pppoe_pass}`}</td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Comentario</strong></td>
                        <td colSpan="7">
                          <span
                            className="editable-inline"
                            onDoubleClick={() => activarEdicion(c.id, "comentario", c.comentario || "", "fibra")}
                          >
                            {editando.id === c.id && editando.campo === "comentario" && editando.tipo === "fibra" ? (
                              <input
                                className="cell-edit-input"
                                autoFocus
                                value={editando.valor}
                                onChange={editarValor}
                                onBlur={guardarEdicion}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") guardarEdicion();
                                  if (e.key === "Escape") cancelarEdicion();
                                }}
                              />
                            ) : (
                              c.comentario || "Sin comentarios"
                            )}
                          </span>
                        </td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Fecha Alta</strong></td>
                        <td colSpan="7">{c.fecha_alta}</td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="paginacion pro ios">
            <button
              className="pg-btn pg-nav"
              disabled={pagina === 1}
              onClick={() => setPagina(pagina - 1)}
              aria-label="Anterior"
            >
              <span className="pg-icon" aria-hidden="true">‹</span>
            </button>

            <div className="pg-pages">
              {pageItems.map((it, idx) =>
                it === "..." ? (
                  <span key={`e-${idx}`} className="pg-ellipsis">…</span>
                ) : (
                  <button
                    key={it}
                    className={`pg-page ${it === pagina ? "active" : ""}`}
                    onClick={() => setPagina(it)}
                  >
                    {it}
                  </button>
                )
              )}
            </div>

            <button
              className="pg-btn pg-nav"
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(pagina + 1)}
              aria-label="Siguiente"
            >
              <span className="pg-icon" aria-hidden="true">›</span>
            </button>
          </div>

          {modalPagosInfo && (
            <ModalPagos cliente={modalPagosInfo} onClose={() => setModalPagosInfo(null)} />
          )}

          {mostrarModal && (
            <ModalAgregarCliente onClose={() => setMostrarModal(false)} onGuardar={guardarCliente} />
          )}

          {modalPago && (
            <ModalPagoCliente
              cliente={modalPago}
              costoPlan={modalPago.costo}
              onClose={() => setModalPago(null)}
              onSubmit={registrarPago}
              submitting={pagoLoading}
            />
          )}

          {modalPagoManual && (
            <ModalPagoManual
              cliente={modalPagoManual}
              onClose={() => setModalPagoManual(null)}
              onSubmit={registrarPagoManual}
              submitting={pagoLoading}
            />
          )}

          {modalActivarOnt && (
            <ModalActivarOnt
              cliente={modalActivarOnt}
              onClose={() => setModalActivarOnt(null)}
              onDone={cargarClientes}
            />
          )}

          {modalPlan && (
            <ModalCambioPlan
              cliente={modalPlan}
              onClose={() => setModalPlan(null)}
              onSuccess={cargarClientes}
              showToast={(msg, tipo) => setToast({ mensaje: msg, tipo })}
            />
          )}
        </>
      )}

      {/* ===========================
          WIFI (clonado fibra)
      =========================== */}
      {tab === "wifi" && (
        <>
          <BarraFiltros
            valor={busquedaWifi}
            placeholder="Buscar por nombre, ID, teléfono…"
            onBuscar={(v) => {
              setBusquedaWifi(v);
              setPaginaWifi(1);
            }}
            filtros={[
              { valor: "fibra", etiqueta: "Fibra" },
              { valor: "wifi", etiqueta: "WiFi" },
            ]}
            filtroActivo={tab}
            onFiltro={setTab}
          />

          <table className="clientes-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Cliente</th>
                <th>Apellidos</th>
                <th>Dirección</th>
                <th>Teléfono</th>
                <th>Colonia</th>
                <th>Plan</th>
                <th>Costo</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {clientesPaginaWifi.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td className="expand-cell">
                      <button className="expand-btn" onClick={() => toggleFilaWifi(c.id)}
                        aria-label={c.open ? "Ocultar detalles" : "Ver detalles"}>
                        {c.open
                          ? <Iconos.desplegar size={16} strokeWidth={2} />
                          : <Iconos.siguiente size={16} strokeWidth={2} />}
                      </button>
                    </td>

                    <td>{c.id}</td>

                    <EditableDesktopCell
                      row={c}
                      tipo="wifi"
                      campo="nombre"
                      valor={`${c.nombre || ""}`.trim()}
                      render={() => `${c.nombre || ""}`.trim()}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="wifi"
                      campo="apellidos"
                      valor={c.apellidos || ""}
                      render={() => c.apellidos || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="wifi"
                      campo="direccion"
                      valor={c.direccion || ""}
                      render={() => c.direccion || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="wifi"
                      campo="telefono"
                      valor={c.telefono || ""}
                      render={() => c.telefono || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />
                    <EditableDesktopCell
                      row={c}
                      tipo="wifi"
                      campo="colonia"
                      valor={c.colonia || ""}
                      render={() => c.colonia || "-"}
                      editando={editando}
                      activarEdicion={activarEdicion}
                      editarValor={editarValor}
                      guardarEdicion={guardarEdicion}
                      cancelarEdicion={cancelarEdicion}
                    />

                    <td>{c.plan || "-"}</td>
                    <td>${c.costo ?? "-"}</td>

                    <td>
                      {/* En wifi el estado es 1/0, no texto: se traduce aquí
                          para que la insignia se vea igual que en fibra. */}
                      <Insignia estado={Number(c.estado) === 1 ? "activo" : "cortado"} />
                    </td>

                    <td>
                      <div className="acciones">
                      <Boton onClick={() => setModalPagoWifi(c)} disabled={pagoLoading}>
                        Pagar
                      </Boton>

                      <AccionFila
                        icono={Iconos.pagos}
                        titulo="Historial de pagos"
                        onClick={() => setModalPagosInfoWifi(c)}
                        disabled={pagoLoading}
                      />
                      <AccionFila
                        icono={Iconos.ubicacion}
                        titulo="Cambio de domicilio"
                        onClick={() => abrirMudarClienteWifi(c)}
                        disabled={pagoLoading}
                      />
                      <AccionFila
                        icono={Iconos.documento}
                        titulo="Pago manual"
                        onClick={() => setModalPagoManualWifi(c)}
                        disabled={pagoLoading}
                      />
                      </div>
                    </td>
                  </tr>

                  {/* ✅ EXPANDIDO EN FILAS (vertical) */}
                  {c.open && (
                    <>
                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Municipio</strong></td>
                        <td colSpan="7">
                          <span
                            className="editable-inline"
                            onDoubleClick={() => activarEdicion(c.id, "municipio", c.municipio || "", "wifi")}
                          >
                            {editando.id === c.id && editando.campo === "municipio" && editando.tipo === "wifi" ? (
                              <input
                                className="cell-edit-input"
                                autoFocus
                                value={editando.valor}
                                onChange={editarValor}
                                onBlur={guardarEdicion}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") guardarEdicion();
                                  if (e.key === "Escape") cancelarEdicion();
                                }}
                              />
                            ) : (
                              c.municipio || "-"
                            )}
                          </span>
                        </td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Nodo</strong></td>
                        <td colSpan="7">{c.nodo || "-"}</td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>IP</strong></td>
                        <td colSpan="7">
                          <span
                            className="editable-inline"
                            onDoubleClick={() => activarEdicion(c.id, "ip", c.ip || " ", "wifi")}
                          >
                            {editando.id === c.id && editando.campo === "ip" && editando.tipo === "wifi" ? (
                              <input
                                className="cell-edit-input"
                                autoFocus
                                value={editando.valor}
                                onChange={editarValor}
                                onBlur={guardarEdicion}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") guardarEdicion();
                                  if (e.key === "Escape") cancelarEdicion();
                                }}
                              />
                            ) : (
                              c.ip || ""
                            )}
                          </span>
                        </td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Equipo</strong></td>
                        <td colSpan="7">{Number(c.equipo) === 1 ? "prestado" : "propio"}</td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Comentario</strong></td>
                        <td colSpan="7">
                          <span
                            className="editable-inline"
                            onDoubleClick={() => activarEdicion(c.id, "comentario", c.comentario || "", "wifi")}
                          >
                            {editando.id === c.id && editando.campo === "comentario" && editando.tipo === "wifi" ? (
                              <input
                                className="cell-edit-input"
                                autoFocus
                                value={editando.valor}
                                onChange={editarValor}
                                onBlur={guardarEdicion}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") guardarEdicion();
                                  if (e.key === "Escape") cancelarEdicion();
                                }}
                              />
                            ) : (
                              c.comentario || "Sin comentarios"
                            )}
                          </span>
                        </td>
                      </tr>

                      <tr className="fila-extra">
                        <td colSpan="3"><strong>Fecha Alta</strong></td>
                        <td colSpan="7">{c.fecha_creacion || "-"}</td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="paginacion pro ios">
            <button
              className="pg-btn pg-nav"
              disabled={paginaWifi === 1}
              onClick={() => setPaginaWifi(paginaWifi - 1)}
              aria-label="Anterior"
            >
              <span className="pg-icon" aria-hidden="true">‹</span>
            </button>

            <div className="pg-pages">
              {pageItemsWifi.map((it, idx) =>
                it === "..." ? (
                  <span key={`we-${idx}`} className="pg-ellipsis">…</span>
                ) : (
                  <button
                    key={`w-${it}`}
                    className={`pg-page ${it === paginaWifi ? "active" : ""}`}
                    onClick={() => setPaginaWifi(it)}
                  >
                    {it}
                  </button>
                )
              )}
            </div>

            <button
              className="pg-btn pg-nav"
              disabled={paginaWifi === totalPaginasWifi}
              onClick={() => setPaginaWifi(paginaWifi + 1)}
              aria-label="Siguiente"
            >
              <span className="pg-icon" aria-hidden="true">›</span>
            </button>
          </div>

          {/* ===== Modales WiFi ===== */}
          {modalPagosInfoWifi && (
            <ModalPagosWifi
              cliente={modalPagosInfoWifi}
              onClose={() => setModalPagosInfoWifi(null)}
            />
          )}

          {mostrarModalWifi && (
            <ModalAgregarClienteWifi
              onClose={() => setMostrarModalWifi(false)}
              onGuardar={guardarClienteWifi}
            />
          )}

          {modalPagoWifi && (
            <ModalPagoClienteWifi
              cliente={modalPagoWifi}
              costoPlan={modalPagoWifi.costo}
              onClose={() => setModalPagoWifi(null)}
              onSubmit={registrarPagoWifi}
              submitting={pagoLoading}
            />
          )}

          {modalPagoManualWifi && (
            <ModalPagoManualWifi
              cliente={modalPagoManualWifi}
              onClose={() => setModalPagoManualWifi(null)}
              onSubmit={registrarPagoManualWifi}
              submitting={pagoLoading}
            />
          )}

          {modalMudarClienteWifi && (
            <ModalMudarClienteWifi
              cliente={modalMudarClienteWifi}     // ✅ AQUI VA EL ROW WIFI
              onClose={() => setModalMudarClienteWifi(null)}
              onGuardar={mudarClienteWifi}
            />
          )}


        </>
      )}

      {/* Overlays globales: van FUERA de las pestañas a propósito.
          Si se montan dentro de {tab === "fibra"} o {tab === "wifi"}, un
          pago hecho en una pestaña deja de verse al cambiar a la otra. Peor
          aún con ProcesoPago: si no está montado, su onClose nunca se
          dispara, y el ticket que espera en ticketPendiente no se muestra
          jamás (así se descubrió: al pagar un cliente WiFi con adeudo, el
          ticket aparecía recién al volver a la pestaña de fibra). */}
      <ProcesoPago
        proceso={procesoPago}
        onClose={() => {
          setProcesoPago(null);
          if (ticketPendiente) {
            setTicketData(ticketPendiente);
            setTicketPendiente(null);
          }
        }}
      />

      {ticketData && <TicketPago ticket={ticketData} onClose={() => setTicketData(null)} />}

      {toast && <Toast mensaje={toast.mensaje} tipo={toast.tipo} onClose={() => setToast(null)} />}
    </div>
  );
}

